import axios from 'axios';
import Config from '../env.config';
import { AUTH_TOKEN_KEY } from '../constants/Values';
import { store } from '../store/instance';
import { dropSession } from '../actions/Auth';
import { setServerError } from '../actions/App';
import { normalizeServerError } from '../util/errorNormalizer';
import { getCookie } from '../util/cookies';

export const CHECK_WS_TOKEN_URL = '/core/notifications/ws/tokens';

// The dataset-activation endpoint answers 503 when the activation sweeper is disabled,
// which is the PACKAGED DEFAULT (`transportation.job.dataset-activation.enabled: false`)
// and therefore the state of every environment except QA. That 503 is not a server
// fault: it is the documented "not enabled here" answer, and Manage Routes V2 renders
// its own copy for it inside the ActivateDialog.
//
// Without this exemption that copy is unreachable — `setServerError` swaps the ENTIRE
// app for a full-page error, unmounting the section and its per-mount editor store, so
// the operator loses the dialog, the draft they were about to activate, and any unsent
// work, in exchange for a page that says nothing about activation. Same idiom and same
// reason as the CHECK_WS_TOKEN_URL exemption above.
export const ACTIVATE_DATASET_URL = '/transportation/routes/dataset/snapshots';
export const ACTIVATE_DATASET_URL_SUFFIX = '/activate';

// Narrow on purpose: ONLY a 503, and only from the activate endpoint. A 500 there is a
// genuine server fault and still takes the full-page path.
function isActivationDisabled(status, url) {
  if (status !== 503 || typeof url !== 'string') return false;
  return url.includes(ACTIVATE_DATASET_URL) && url.endsWith(ACTIVATE_DATASET_URL_SUFFIX);
}

const instance = axios.create({ baseURL: Config.API });

instance.interceptors.request.use(config => ({
  ...config,
  headers: {
    ...config.headers,
    'Content-Type': 'application/json',
  },
}));

instance.interceptors.response.use(
  response => response.data,
  async error => {
    if (error && error.response && error.response.status) {
      if (error.response.status === 401 && getCookie(AUTH_TOKEN_KEY)) {
        // TODO: add redirect to page from which we has been logged out
        store.dispatch(dropSession());
      }

      // !error.config.url.includes(CHECK_WS_TOKEN_URL)
      // -> it is check to avoid 502 page displaying when redeploy in progress
      if (
        error.response.status >= 500 &&
        !error.config.url.includes(CHECK_WS_TOKEN_URL) &&
        !isActivationDisabled(error.response.status, error.config.url)
      ) {
        store.dispatch(setServerError(error.response.status));
      }
    }

    let messages = [];
    if (error.response) {
      messages = await normalizeServerError(error.response.data);
    }
    const errorResponse = {
      messages,
      errors: (error.response && error.response.data && error.response.data.errors) || null,
      status: (error.response && error.response.status) || '',
    };

    return Promise.reject(errorResponse);
  },
);

export default instance;
